import { useCallback, useEffect, useState } from 'react';

import type { AdminCreatorReferralsResponse, AdminReferralsResponse } from '@nexus/shared';

import { ApiError, apiGet } from '../../lib/api';
import {
  Alert,
  Badge,
  Card,
  DataTable,
  EmptyState,
  LinkButton,
  LoadErrorPage,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  When,
  type Column,
} from '../../ui';

/**
 * The referral programme, for the operator.
 *
 * ## The reconciliation is the headline
 *
 * Every bonus is funded from one ledger account — the platform's referral pot —
 * so that account's balance must be exactly minus the sum of the bonuses that
 * have not been reversed. The banner at the top of this page is that identity,
 * and it is the same kind of statement as the trial balance's `balanced`: the
 * only way this feature can be quietly wrong is for a `ReferralBonus` row and
 * its ledger legs to disagree, and nothing else in the system would notice.
 *
 * It does not turn the page red on its own the way a failed trial balance does.
 * A referral pot that does not reconcile is a defect to chase; a general ledger
 * that does not balance is a system that must not pay anybody.
 */
export function AdminReferrals(): React.JSX.Element {
  const [data, setData] = useState<AdminReferralsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<AdminCreatorReferralsResponse | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<AdminReferralsResponse>('/admin/referrals'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the referrals.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openCreator(creatorId: string): Promise<void> {
    try {
      setOpen(await apiGet<AdminCreatorReferralsResponse>(`/admin/referrals/${creatorId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that creator.');
    }
  }

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Operator"
        title="Referrals"
        heading="Could not load the referrals."
        onRetry={() => void load()}
      >
        <span>{error}</span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Operator" title="Referrals" stats />;

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Referrals"
        lead="Every bonus is funded from the platform's own referral pot, never from a campaign budget."
      />

      {!data.totals.potReconciles && (
        <Alert tone="critical" title="The referral pot does not reconcile.">
          <span>
            The ledger says <Money cents={data.totals.platformPotCents} /> has left the referral
            pot; the bonus rows add up to <Money cents={data.totals.accruedCents} />. A
            `ReferralBonus` row and its ledger legs disagree — check{' '}
            <code className="chip quiet">/admin/ledger/trial-balance</code> before approving a
            payout batch.
          </span>
        </Alert>
      )}

      <Card variant="feature">
        <StatGrid>
          <Stat
            label="Referred creators"
            value={<Num value={data.totals.referredCreators} />}
            note={`${data.totals.qualifiedCreators} qualified`}
          />
          <Stat
            label="Bonuses"
            value={<Num value={data.totals.bonuses} />}
            note={`${data.totals.reversedBonuses} reversed`}
          />
          <Stat
            label="Accrued"
            value={<Money cents={data.totals.accruedCents} />}
            note="Not reversed. What the programme has cost."
            emphasis
          />
          <Stat
            label="Paid out"
            value={<Money cents={data.totals.paidCents} />}
            note="Bonuses that have actually left."
          />
        </StatGrid>
        <p className="muted">
          The referral pot stands at <Money cents={data.totals.platformPotCents} />, which is
          negative by design: it is the account the money comes out of, the way client funds go
          negative as clients pay in. It must equal minus the accrued figure exactly.
        </p>
      </Card>

      <Card title="Referrers" sub="Everybody with a code, and what they have brought in.">
        <ReferrerTable data={data} onOpen={(id) => void openCreator(id)} />
      </Card>

      {open && <CreatorDetail detail={open} onClose={() => setOpen(null)} />}

      {error && <Alert tone="critical">{error}</Alert>}
    </>
  );
}

function ReferrerTable({
  data,
  onOpen,
}: {
  data: AdminReferralsResponse;
  onOpen: (creatorId: string) => void;
}): React.JSX.Element {
  type Row = AdminReferralsResponse['referrers'][number];
  const columns: Array<Column<Row>> = [
    {
      key: 'who',
      header: 'Creator',
      primary: true,
      cell: (row) => (
        <>
          <LinkButton onClick={() => onOpen(row.creatorId)}>
            {row.displayName ?? row.email}
          </LinkButton>
          <span className="cell-note">{row.email}</span>
        </>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      cell: (row) =>
        row.code ? <code className="chip quiet">{row.code}</code> : <span className="muted">—</span>,
    },
    { key: 'referred', header: 'Referred', numeric: true, align: 'end', cell: (r) => <Num value={r.referred} /> },
    { key: 'qualified', header: 'Qualified', numeric: true, align: 'end', cell: (r) => <Num value={r.qualified} /> },
    {
      key: 'earned',
      header: 'Earned',
      numeric: true,
      align: 'end',
      cell: (row) => <Money cents={row.earnedCents} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.referrers}
      getKey={(row) => row.creatorId}
      empty={
        <EmptyState title="Nobody has referred anybody yet.">
          A code is issued the first time a creator opens their Referrals tab.
        </EmptyState>
      }
    />
  );
}

function CreatorDetail({
  detail,
  onClose,
}: {
  detail: AdminCreatorReferralsResponse;
  onClose: () => void;
}): React.JSX.Element {
  type Bonus = AdminCreatorReferralsResponse['bonuses'][number];
  const columns: Array<Column<Bonus>> = [
    {
      key: 'status',
      header: 'Status',
      primary: true,
      cell: (row) => (
        <>
          <Badge tone={row.status === 'REVERSED' ? 'critical' : 'positive'}>
            {row.status === 'REVERSED' ? 'Reversed' : 'Accrued'}
          </Badge>
          <span className="cell-note">earning {row.bonusEarningStatus.toLowerCase()}</span>
          {row.reversalReason && <span className="cell-note">{row.reversalReason}</span>}
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (row) => <Money cents={row.amountCents} />,
    },
    { key: 'when', header: 'Accrued', cell: (row) => <When value={row.createdAt} /> },
    {
      key: 'source',
      header: 'From earning',
      cell: (row) => <code className="chip quiet">{row.sourceEarningId.slice(0, 8)}</code>,
    },
  ];

  return (
    <Card
      title={detail.creator.displayName ?? detail.creator.email}
      sub={`Code ${detail.creator.code ?? 'not issued yet'}`}
      aside={<LinkButton onClick={onClose}>Close</LinkButton>}
    >
      {detail.creator.referredBy && (
        <p className="muted">
          Referred by {detail.creator.referredBy.displayName ?? detail.creator.referredBy.email} (
          {detail.creator.referredBy.email}) on <When value={detail.creator.referredAt} />.
        </p>
      )}
      <DataTable
        columns={columns}
        rows={detail.bonuses}
        getKey={(row) => row.id}
        empty={<EmptyState title="No bonuses yet.">Their friends have not earned anything payable.</EmptyState>}
      />
    </Card>
  );
}
